import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '../config/api.config';

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

// ===== État partagé du refresh : un seul refresh à la fois, les autres requêtes attendent =====
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token as string)));
    failedQueue = [];
};

export const getAuthInfo = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return {
        garageId: user._id || '',
        garageMail: user.email || '',
        token: localStorage.getItem('token'),
        refreshToken: localStorage.getItem('refreshToken'),
    };
};

const clearAuthData = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
};

const redirectToLogin = (reason?: string) => {
    clearAuthData();
    if (window.location.pathname.startsWith('/login')) return;
    window.location.href = reason ? `/login?reason=${reason}` : '/login';
};

/**
 * Crée une instance axios authentifiée : le token est relu à chaque requête
 * (il change après un login ou un refresh) et les erreurs d'auth sont gérées
 * de façon centralisée (refresh, compte désactivé, session invalide).
 */
export const createAuthAxios = () => {
    const instance = axios.create({ baseURL: API_URL });

    instance.interceptors.request.use((config) => {
        const { token } = getAuthInfo();
        if (token) {
            config.headers.set('Authorization', `Bearer ${token}`);
        }
        return config;
    });

    instance.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as RetriableRequest | undefined;
            const status = error.response?.status;
            const errorData = error.response?.data as any;

            // Compte verrouillé (423) / rate limiting (429) : l'appelant affiche le message
            if (status === 423 || status === 429) {
                return Promise.reject(error);
            }

            // Compte désactivé ou supprimé par l'admin
            if (status === 403 && errorData?.error === 'accountDeactivated') {
                redirectToLogin('account_deactivated');
                return Promise.reject(error);
            }

            if (status === 401 && originalRequest && !originalRequest._retry) {
                if (errorData?.error === 'tokenExpired' && errorData?.needsRefresh) {
                    // Un refresh est déjà en cours : on attend son résultat puis on rejoue la requête
                    if (isRefreshing) {
                        return new Promise<string>((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        }).then((token) => {
                            originalRequest.headers.set('Authorization', `Bearer ${token}`);
                            return instance(originalRequest);
                        });
                    }

                    // Le token a déjà été renouvelé depuis l'envoi de cette requête : on la rejoue sans refresh
                    const sentAuth = String(originalRequest.headers.get('Authorization') || '');
                    const currentToken = getAuthInfo().token;
                    if (currentToken && sentAuth && sentAuth !== `Bearer ${currentToken}`) {
                        originalRequest._retry = true;
                        originalRequest.headers.set('Authorization', `Bearer ${currentToken}`);
                        return instance(originalRequest);
                    }

                    originalRequest._retry = true;
                    isRefreshing = true;

                    try {
                        const { refreshToken } = getAuthInfo();
                        if (!refreshToken) throw new Error('Aucun refresh token disponible');

                        // axios nu : cet appel ne doit pas repasser par les intercepteurs
                        const refreshResponse = await axios.post(
                            `${API_URL}${errorData.refreshEndpoint}`,
                            { refreshToken }
                        );
                        const data = refreshResponse.data;

                        if (!data?.success || !data.token) {
                            throw new Error('Échec du refresh : réponse invalide');
                        }

                        localStorage.setItem('token', data.token);
                        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
                        if (data.user) {
                            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                            localStorage.setItem('user', JSON.stringify({ ...currentUser, ...data.user }));
                        }

                        processQueue(null, data.token);
                        originalRequest.headers.set('Authorization', `Bearer ${data.token}`);
                        return instance(originalRequest);
                    } catch (refreshError: any) {
                        processQueue(refreshError, null);
                        const deactivated =
                            refreshError?.response?.status === 403 &&
                            refreshError?.response?.data?.error === 'accountDeactivated';
                        redirectToLogin(deactivated ? 'account_deactivated' : undefined);
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing = false;
                    }
                }

                // Token invalide (pas simplement expiré)
                if (errorData?.needsAuth) {
                    redirectToLogin();
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export const authAxios = createAuthAxios();
